import { safeSessionGet, safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";
import { reportChunkRecovery, type ChunkRecoveryTrigger } from "@/lib/js-error-beacon";
import {
  CHUNK_RECOVERY_CONFIRM_MS,
  CHUNK_RECOVERY_HISTORY_KEY,
  CHUNK_RECOVERY_MARKER_KEY,
  CHUNK_RECOVERY_MARKER_TTL_MS,
  CHUNK_RELOAD_COOLDOWN_MS,
} from "@/lib/chunk-recovery-contract";
import { proveCurrentDocumentChunkGraph } from "@/lib/chunk-recovery-probe";

export { CHUNK_RECOVERY_CONFIRM_MS, CHUNK_RELOAD_COOLDOWN_MS };
const MODULE_DOCUMENT_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

interface ChunkRecoveryMarker {
  attemptId: string;
  startedAt: number;
  sourcePath: string;
  sourceDocumentId: string;
  recoveryDocumentId?: string;
  trigger: ChunkRecoveryTrigger;
  assetPath?: string;
  confirmedAt?: number;
  unrecoveredAt?: number;
}

interface ChunkFailure {
  trigger: ChunkRecoveryTrigger;
  assetPath?: string;
}

interface ConfirmationOptions {
  now?: number;
  documentId?: string;
  schedule?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  proveCurrentChunkGraph?: () => Promise<boolean>;
}

const scheduleTimeout = (callback: () => void, delay: number): ReturnType<typeof setTimeout> =>
  setTimeout(callback, delay);
const noConfirmation = (): undefined => undefined;

function parseMarker(value: unknown): ChunkRecoveryMarker | null {
  try {
    const candidate = typeof value === "string" ? JSON.parse(value) : value;
    if (!candidate || typeof candidate !== "object") return null;
    const marker = candidate as Partial<ChunkRecoveryMarker>;
    if (
      typeof marker.attemptId !== "string" ||
      typeof marker.startedAt !== "number" ||
      typeof marker.sourcePath !== "string" ||
      typeof marker.sourceDocumentId !== "string" ||
      !["resource", "runtime", "rejection"].includes(String(marker.trigger))
    ) {
      return null;
    }
    return marker as ChunkRecoveryMarker;
  } catch {
    return null;
  }
}

function historyMarker(): ChunkRecoveryMarker | null {
  try {
    const state = window.history.state as Record<string, unknown> | null;
    return parseMarker(state?.[CHUNK_RECOVERY_HISTORY_KEY]);
  } catch {
    return null;
  }
}

function readMarker(): ChunkRecoveryMarker | null {
  return parseMarker(safeSessionGet(CHUNK_RECOVERY_MARKER_KEY)) ?? historyMarker();
}

function writeMarker(marker: ChunkRecoveryMarker): void {
  safeSessionSet(CHUNK_RECOVERY_MARKER_KEY, JSON.stringify(marker));
  try {
    const current = window.history.state;
    const state = current && typeof current === "object" ? current : {};
    window.history.replaceState({ ...state, [CHUNK_RECOVERY_HISTORY_KEY]: marker }, "");
  } catch {
    // sessionStorage remains the primary copy when History state is restricted.
  }
}

function clearMarker(): void {
  safeSessionRemove(CHUNK_RECOVERY_MARKER_KEY);
  try {
    const current = window.history.state;
    if (!current || typeof current !== "object") return;
    const { [CHUNK_RECOVERY_HISTORY_KEY]: _removed, ...rest } = current as Record<string, unknown>;
    window.history.replaceState(rest, "");
  } catch {
    // Best effort only; the marker expires by timestamp even if removal fails.
  }
}

function safePathname(): string {
  try {
    return window.location.pathname || "/";
  } catch {
    return "/";
  }
}

function currentDocumentId(): string {
  try {
    const timeOrigin = window.performance?.timeOrigin;
    if (Number.isFinite(timeOrigin)) return `time-origin:${timeOrigin}`;
  } catch {
    // Fall back to the module instance below.
  }
  return `module:${MODULE_DOCUMENT_ID}`;
}

function normalizedAssetPath(assetPath?: string): string | undefined {
  const path = assetPath?.split(/[?#]/, 1)[0];
  return path?.startsWith("/_next/static/") ? path : undefined;
}

function newMarker(failure: ChunkFailure, now: number, documentId: string): ChunkRecoveryMarker {
  const assetPath = normalizedAssetPath(failure.assetPath);
  return {
    attemptId: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    startedAt: now,
    sourcePath: safePathname(),
    sourceDocumentId: documentId,
    trigger: failure.trigger,
    ...(assetPath ? { assetPath } : {}),
  };
}

function emitRepeatFailure(
  marker: ChunkRecoveryMarker,
  failure: ChunkFailure,
  now: number,
  documentId: string
): void {
  if (marker.unrecoveredAt) return;
  const failed = {
    ...marker,
    recoveryDocumentId: marker.recoveryDocumentId ?? documentId,
    unrecoveredAt: now,
  };
  writeMarker(failed);
  reportChunkRecovery({
    attemptId: failed.attemptId,
    state: "unrecovered",
    trigger: failure.trigger,
    assetPath: normalizedAssetPath(failure.assetPath) ?? failed.assetPath,
    verification: "repeat_failure",
  });
}

/** Return true only when the caller should perform one hard reload. */
export function requestChunkReload(
  failure: ChunkFailure,
  now = Date.now(),
  documentId = currentDocumentId()
): boolean {
  const previous = readMarker();
  if (previous && now - previous.startedAt < CHUNK_RELOAD_COOLDOWN_MS) {
    // A single network failure commonly surfaces through both resource `error`
    // and `unhandledrejection`. The original document has already requested its
    // reload, so these duplicate observations are neither a second attempt nor
    // proof that the reload failed.
    if (previous.sourceDocumentId === documentId) return false;

    // Only the one document created by this recovery attempt may terminalize it.
    // A later unrelated navigation must not inherit and fail an old marker.
    if (previous.recoveryDocumentId && previous.recoveryDocumentId !== documentId) return false;

    emitRepeatFailure(previous, failure, now, documentId);
    return false;
  }

  if (previous && now - previous.startedAt >= CHUNK_RECOVERY_MARKER_TTL_MS) clearMarker();
  const marker = newMarker(failure, now, documentId);
  writeMarker(marker);
  reportChunkRecovery({
    attemptId: marker.attemptId,
    state: "reload_started",
    trigger: marker.trigger,
    assetPath: marker.assetPath,
  });
  return true;
}

function activeRecoveryMarker(
  marker: ChunkRecoveryMarker,
  documentId: string,
  isCancelled: () => boolean
): ChunkRecoveryMarker | null {
  const current = readMarker();
  if (!current) return null;
  if (current.attemptId !== marker.attemptId) return null;
  if (current.recoveryDocumentId !== documentId) return null;
  if (current.unrecoveredAt) return null;
  if (isCancelled()) return null;
  return current;
}

function emitRecovered(marker: ChunkRecoveryMarker): void {
  const confirmed = { ...marker, confirmedAt: Date.now() };
  writeMarker(confirmed);
  reportChunkRecovery({
    attemptId: confirmed.attemptId,
    state: "recovered",
    trigger: confirmed.trigger,
    assetPath: confirmed.assetPath,
    verification: "current_chunk_graph",
  });
}

async function confirmRecovery(
  marker: ChunkRecoveryMarker,
  documentId: string,
  proveCurrentChunkGraph: () => Promise<boolean>,
  isCancelled: () => boolean
): Promise<void> {
  if (!activeRecoveryMarker(marker, documentId, isCancelled)) return;
  // An inconclusive graph proof is not evidence that the user stayed broken.
  // Keep the attempt non-terminal; only a repeat chunk failure emitted from
  // this recovery document may produce the unrecovered verdict above.
  if (!(await proveCurrentChunkGraph())) return;

  const latest = activeRecoveryMarker(marker, documentId, isCancelled);
  if (latest) emitRecovered(latest);
}

function markerForConfirmation(now: number, documentId: string): ChunkRecoveryMarker | null {
  const marker = readMarker();
  if (!marker) return null;
  if (now - marker.startedAt >= CHUNK_RECOVERY_MARKER_TTL_MS) {
    clearMarker();
    return null;
  }
  if (marker.confirmedAt || marker.unrecoveredAt) return null;
  if (marker.sourceDocumentId === documentId) return null;
  if (safePathname() !== marker.sourcePath) {
    clearMarker();
    return null;
  }
  if (marker.recoveryDocumentId && marker.recoveryDocumentId !== documentId) return null;
  return marker;
}

/**
 * Confirm only inside the new document created by the hard reload. Its declared
 * current JS/CSS graph is fetched and MIME-checked; the retired asset that caused
 * the reload is deliberately not evidence about the replacement document.
 */
export function armChunkRecoveryConfirmation(options: ConfirmationOptions = {}): () => void {
  const {
    now = Date.now(),
    documentId = currentDocumentId(),
    schedule = scheduleTimeout,
    proveCurrentChunkGraph = proveCurrentDocumentChunkGraph,
  } = options;
  const marker = markerForConfirmation(now, documentId);
  if (!marker) return noConfirmation;

  const armed = { ...marker, recoveryDocumentId: documentId };
  writeMarker(armed);
  let cancelled = false;
  const timer = schedule(() => {
    void confirmRecovery(armed, documentId, proveCurrentChunkGraph, () => cancelled);
  }, CHUNK_RECOVERY_CONFIRM_MS);
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}

/** Test-only reset for the dual storage loop guard. */
export function resetChunkRecoveryForTests(): void {
  clearMarker();
}
